import { publicPortalBody } from "./portal-errors";
import { UserFacingError, errorMessage, isCancellation, type ErrorOptions } from "@streamoid/errors";
export const PORTAL_API_BASE = "/api/portal/v1";

export type PortalRole = "staff" | "participant" | "operator" | "admin";

export type TeamMember = {
  email: string;
  subject?: string;
  display_name?: string;
  role: "member" | "admin";
  access: "active" | "denied";
  last_signed_in_at?: string;
  created_at: string;
  updated_at: string;
};

export type PortalUser = {
  id: string;
  email: string;
  name: string;
  role: PortalRole;
  avatar_url?: string;
};

export type RequestState =
  | "needs_input"
  | "plan_approval"
  | "queued"
  | "building"
  | "previewing"
  | "verifying"
  | "awaiting_feedback"
  | "changes_requested"
  | "merge_ready"
  | "merge_blocked"
  | "merged"
  | "closed"
  | "failed"
  | "cancelled";

export type RequestType = "information" | "work";

/// `guided` is the conversational flow: questions, a plan to approve, preview QA.
/// `auto` asks nothing — Netra finds the repositories, opens the pull requests,
/// and closes the request with their links.
export type RequestMode = "guided" | "auto";

export type AutopilotPullRequest = {
  repository: string;
  number: number;
  url: string;
  title: string;
  branch?: string;
  base?: string;
};

export type AutopilotRun = {
  job_id: string;
  status: "queued" | "running" | "finalizing" | "completed" | "failed" | "aborted";
  provider?: string;
  summary?: string;
  pull_requests: AutopilotPullRequest[];
  error?: string;
  started_at?: string;
  completed_at?: string;
};

export type RequestActor = Pick<PortalUser, "id" | "name" | "email"> & {
  role?: PortalRole | "agent" | "system";
};

export type RequestMessage = {
  id: string;
  author: RequestActor;
  body: string;
  kind?: "message" | "question" | "decision" | "system" | "clarification" | "information_answer" | "autopilot";
  created_at: string;
};

export type RequestAttachment = {
  id: string;
  message_id?: string;
  file_name: string;
  content_type: string;
  size_bytes: number;
  sha256: string;
  created_at: string;
  url: string;
};

export type RequestRequirements = {
  repository?: string;
  title?: string;
  problem?: string;
  reproduction?: string;
  expected_behavior?: string;
  actual_behavior?: string;
  impact?: string;
  environment?: string;
  scope?: string;
  acceptance_criteria?: string[];
};

export type RequestPlan = {
  version: number;
  status: "draft" | "pending" | "pending_approval" | "approved" | "superseded";
  title?: string;
  summary?: string;
  content: string;
  change_summary?: string;
  created_at: string;
  approved_at?: string;
  approved_by?: RequestActor;
};

export type WorkflowStage = {
  name: string;
  status: "pending" | "running" | "passed" | "failed" | "skipped";
  started_at?: string;
  completed_at?: string;
  detail?: string;
};

export type WorkflowCycle = {
  id: string;
  number: number;
  status: string;
  head_sha?: string;
  created_at: string;
  stages?: WorkflowStage[];
  /// Raw gate values, kept alongside the normalized stages so the merge views
  /// can tell "no checks configured" apart from "no checks reported yet".
  ci_status?: string;
  preview_status?: string;
  qa_status?: string;
};

export type DeploymentEvidence = {
  status: "pending" | "running" | "success" | "failed" | "unavailable" | string;
  provider?: string;
  environment?: string;
  sha?: string;
  url?: string;
  detail?: string;
  deployed_at?: string;
};

export type RequestPreview = {
  id?: string;
  status: string;
  url?: string;
  head_sha: string;
  exact_head: boolean;
  expires_at?: string;
};

export type QaStep = {
  id?: string;
  title: string;
  status: "pending" | "running" | "passed" | "failed" | "skipped";
  duration_ms?: number;
  detail?: string;
};

export type QaRun = {
  id: string;
  status: string;
  head_sha: string;
  passed: number;
  failed: number;
  skipped: number;
  steps?: QaStep[];
};

export type EvidenceArtifact = {
  id: string;
  kind: "video" | "screenshot" | "test_report" | "trace" | "log" | string;
  title: string;
  url?: string;
  content_type?: string;
  head_sha: string;
  sha256?: string;
  summary?: string;
  created_at: string;
};

export type GithubDelivery = {
  issue_number?: number;
  issue_url?: string;
  pull_request_number?: number;
  pull_request_url?: string;
  mergeable_state?: string;
  head_sha?: string;
  base_sha?: string;
};

export type PortalRequestSummary = {
  id: string;
  request_type: RequestType;
  mode: RequestMode;
  autopilot?: AutopilotRun;
  title: string;
  repository: string;
  repository_confirmed: boolean;
  state: RequestState;
  priority?: "urgent" | "high" | "normal" | "low";
  requester: RequestActor;
  participant_count?: number;
  unread_count?: number;
  latest_message?: string;
  current_plan_version?: number;
  current_cycle?: number;
  github?: GithubDelivery;
  created_at: string;
  updated_at: string;
};

export type PortalRequestDetail = PortalRequestSummary & {
  information_answer_pending: boolean;
  intake_analysis_pending?: boolean;
  requirements?: RequestRequirements;
  messages: RequestMessage[];
  attachments: RequestAttachment[];
  plans: RequestPlan[];
  cycles: WorkflowCycle[];
  preview?: RequestPreview;
  requester_review?: {
    head_sha: string;
    approved_by?: string;
    approved_at?: string;
  };
  deployment?: DeploymentEvidence;
  /// Environment tiers reached by the merged revision, test before production.
  deployments?: EnvironmentDeployment[];
  qa_run?: QaRun;
  artifacts: EvidenceArtifact[];
  timeline?: Array<{
    id: string;
    kind: string;
    title: string;
    detail?: string;
    actor?: RequestActor;
    created_at: string;
  }>;
};

export type PortalNotification = {
  id: string;
  request_id?: string;
  title: string;
  body?: string;
  kind?: string;
  read_at?: string;
  created_at: string;
};

/// One environment tier a merged revision reaches on its way to being live.
export type EnvironmentDeployment = {
  tier: "test" | "production" | string;
  environment: string;
  /// success (provider-confirmed), merged (in the branch, no provider evidence),
  /// running, failed, pending (not there yet), or unavailable.
  status: string;
  provider?: string;
  url?: string;
  detail?: string;
  deployed_at?: string;
};

export type MergeReadinessItem = {
  request_id: string;
  title: string;
  repository: string;
  pull_request_number: number;
  pull_request_url: string;
  head_sha: string;
  base_sha?: string;
  state: "ready" | "blocked" | "checking" | "conflicting" | "merged" | "live" | string;
  request_state: RequestState;
  mergeable?: boolean;
  exact_head_verified?: boolean;
  mergeable_state?: string;
  base_drift?: number;
  ci_status?: string;
  preview_status?: string;
  qa_status?: string;
  requester_approved_head_sha?: string;
  requester_approved_by?: string;
  requester_approved_at?: string;
  deployments?: EnvironmentDeployment[];
  unresolved_threads?: number;
  conflict_attempts?: number;
  conflict_detail?: string;
  updated_at: string;
};

export type MergeMethod = "squash" | "merge" | "rebase";

export type PortalRepository = {
  repository: string;
  enabled: boolean;
  qa_config_path?: string;
  secret_aliases?: string[];
  retention_days?: number;
  max_concurrency?: number;
  updated_at?: string;
};

export type UpdatePortalRepositoryInput = {
  enabled: boolean;
  qa_config_path: string;
  secret_aliases: string[];
  retention_days: number;
  max_concurrency: number;
  updated_at?: string;
};

export type CreatePortalRequestInput = {
  repository: string;
  title: string;
  problem: string;
  reproduction: string;
  expected_behavior: string;
  actual_behavior: string;
  impact: string;
  environment: string;
  scope: string;
  acceptance_criteria: string[];
  initial_message?: string;
  defer_agent?: boolean;
  mode?: RequestMode;
};

type ListEnvelope<T, K extends string> = { [P in K]: T[] } & {
  next_cursor?: string;
  total?: number;
};

type RawActor = {
  subject: string;
  email: string;
  display_name?: string;
  role: PortalRole;
};

type RawRequirements = Required<CreatePortalRequestInput>;

type RawCase = {
  id: string;
  request_type?: RequestType;
  requirements: RawRequirements;
  status: RequestState;
  created_by: string;
  created_at: string;
  updated_at: string;
  latest_plan_version?: number;
  unread_count?: number;
  repository_confirmed?: boolean;
  mode?: RequestMode;
  autopilot?: AutopilotRun | null;
};

type RawMessage = {
  id: string;
  case_id: string;
  author_email: string;
  author_name?: string;
  kind: string;
  body: string;
  created_at: string;
};

type RawPlan = {
  case_id: string;
  version: number;
  body: string;
  requirements: RawRequirements;
  status: RequestPlan["status"];
  created_by: string;
  created_at: string;
  approved_at?: string;
};

type RawCycle = {
  id: string;
  case_id: string;
  plan_version: number;
  cycle_number: number;
  status: string;
  issue_number?: number;
  issue_url?: string;
  pr_number?: number;
  pr_url?: string;
  head_sha?: string;
  preview_head_sha?: string;
  preview_url?: string;
  qa_head_sha?: string;
  ci_status?: string;
  preview_status?: string;
  qa_status?: string;
  requester_approved_head_sha?: string;
  requester_approved_by?: string;
  requester_approved_at?: string;
  deployment_status?: string;
  deployment_provider?: string;
  deployment_environment?: string;
  deployment_sha?: string;
  deployment_url?: string;
  deployment_detail?: string;
  deployed_at?: string;
  last_error?: string;
  created_at: string;
  updated_at: string;
};

type RawQaRun = {
  id: string;
  head_sha: string;
  kind: string;
  status: string;
  report_json?: Record<string, unknown>;
  created_at: string;
  completed_at?: string;
};

type RawArtifact = {
  id: string;
  head_sha: string;
  kind: string;
  object_key?: string;
  title?: string;
  sha256?: string;
  content_type?: string;
  size_bytes?: number;
  created_at: string;
};

type RawCaseDetail = {
  case: RawCase;
  information_answer_pending?: boolean;
  intake_analysis_pending?: boolean;
  messages: RawMessage[];
  attachments?: Array<{
    id: string;
    message_id?: string;
    file_name: string;
    content_type: string;
    size_bytes: number;
    sha256: string;
    created_at: string;
  }>;
  plans: RawPlan[];
  cycles: RawCycle[];
  qa_runs: RawQaRun[];
  artifacts: RawArtifact[];
  conflict_attempts?: Array<{ status: string; explanation?: string; created_at: string }>;
  deployments?: EnvironmentDeployment[];
};

type RawNotification = {
  id: string;
  case_id?: string;
  kind?: string;
  title: string;
  body?: string;
  read_at?: string;
  created_at: string;
};

type RawRepository = {
  repository: string;
  pilot_enabled: boolean;
  qa_config_summary?: string;
  secret_refs?: string[];
  retention_days?: number;
  concurrency?: number;
  updated_at?: string;
};

type RawMergeReadiness = {
  case_id: string;
  repository: string;
  title: string;
  status: RequestState;
  issue_url?: string;
  pr_url?: string;
  head_sha?: string;
  base_sha?: string;
  base_drift?: number;
  preview_head_sha?: string;
  qa_head_sha?: string;
  ci_status?: string;
  preview_status?: string;
  qa_status?: string;
  mergeable?: boolean;
  exact_head_verified?: boolean;
  conflict_attempts?: number;
  human_merge_url?: string;
  mergeable_state?: string;
  pr_number?: number;
  requester_approved_head_sha?: string;
  requester_approved_by?: string;
  requester_approved_at?: string;
  deployments?: EnvironmentDeployment[];
};

/// Which shelf of the merge desk a managed pull request belongs on.
///
/// `live` and `merged` are kept apart from the open work: a row that has already
/// shipped must never read like something still waiting for a human.
export function mergeDeskState(item: {
  status: RequestState;
  mergeable?: boolean;
  exact_head_verified?: boolean;
  conflict_attempts?: number;
  deployments?: EnvironmentDeployment[];
}): MergeReadinessItem["state"] {
  const reached = (tier: string) => (item.deployments ?? []).some(
    (deployment) => deployment.tier === tier && ["success", "merged"].includes(deployment.status),
  );
  if (item.status === "merged") return reached("production") ? "live" : "merged";
  if (item.status === "merge_ready" && item.mergeable && item.exact_head_verified) return "ready";
  if (item.status === "merge_blocked") return (item.conflict_attempts ?? 0) > 0 ? "conflicting" : "blocked";
  return "checking";
}

export class PortalApiError extends UserFacingError {
  readonly detail: unknown;
  constructor(failure: unknown, status: number, detail?: unknown, options: ErrorOptions = {}) {
    super(failure, { ...options, status, body: publicPortalBody(detail) });
    this.name = 'PortalApiError';
    this.message = errorMessage(this);
    this.detail = detail;
  }
}

async function requestJson<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  const method = (init?.method ?? "GET").toUpperCase();
  if (init?.body && !headers.has("content-type")) headers.set("content-type", "application/json");
  headers.set("accept", "application/json");
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) headers.set("X-Netra-Portal-CSRF", "1");
  let response: Response;
  try {
    response = await fetch(`${PORTAL_API_BASE}${path}`, {
    ...init,
    headers,
    credentials: "same-origin",
    cache: init?.method && init.method !== "GET" ? "no-store" : init?.cache ?? "no-store",
  });
  } catch (cause) {
    if (isCancellation(cause)) throw cause;
    throw new PortalApiError(cause, 0, undefined, { method: init?.method || "GET" });
  }
  const body = await response.json().catch((cause) => { if (isCancellation(cause)) throw cause; return null; }) as T | { error?: string; message?: string } | null;
  if (!response.ok) {
    const errorBody = body as { error?: string; message?: string } | null;
    throw new PortalApiError(
      errorBody?.message ?? errorBody?.error ?? `Portal request failed (${response.status})`,
      response.status,
      body,
      { method: init?.method || "GET", reference: response.headers.get("X-Error-Ref"), retryAfter: response.headers.get("Retry-After") },
    );
  }
  return body as T;
}

function unwrapList<T, K extends string>(value: T[] | ListEnvelope<T, K> | { items: T[] }, key: K): T[] {
  if (Array.isArray(value)) return value;
  if ("items" in value) return value.items ?? [];
  return value[key] ?? [];
}

function actorFromEmail(email: string, name?: string): RequestActor {
  const agent = /^(netra|agent|system)(@|$)/i.test(email) || /^(netra|agent|system)$/i.test(name ?? "");
  return { id: email, email, name: name || email, role: agent ? "agent" : undefined };
}

function mapSummary(caseRecord: RawCase): PortalRequestSummary {
  return {
    id: caseRecord.id,
    request_type: caseRecord.request_type ?? "work",
    mode: caseRecord.mode ?? "guided",
    autopilot: caseRecord.autopilot ?? undefined,
    title: caseRecord.requirements.title,
    repository: caseRecord.requirements.repository,
    repository_confirmed: caseRecord.repository_confirmed ?? true,
    state: caseRecord.status,
    requester: actorFromEmail(caseRecord.created_by),
    unread_count: caseRecord.unread_count ?? 0,
    latest_message: caseRecord.requirements.problem,
    current_plan_version: caseRecord.latest_plan_version,
    created_at: caseRecord.created_at,
    updated_at: caseRecord.updated_at,
  };
}

function numericReportValue(report: Record<string, unknown> | undefined, key: string) {
  const value = report?.[key];
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function mapDetail(raw: RawCaseDetail): PortalRequestDetail {
  const latestCycle = raw.cycles.at(-1);
  const latestQa = raw.qa_runs.at(-1);
  const report = latestQa?.report_json;
  const reportSteps = Array.isArray(report?.steps) ? report.steps : [];
  const expectedHead = latestCycle?.head_sha;
  return {
    ...mapSummary(raw.case),
    information_answer_pending: raw.information_answer_pending ?? false,
    intake_analysis_pending: raw.intake_analysis_pending ?? false,
    requirements: raw.case.requirements,
    messages: raw.messages.map((message) => ({
      id: message.id,
      author: actorFromEmail(message.author_email, message.author_name),
      body: message.body,
      kind: ["message", "question", "decision", "system", "clarification", "information_answer", "autopilot"].includes(message.kind)
        ? message.kind as RequestMessage["kind"]
        : "message",
      created_at: message.created_at,
    })),
    attachments: (raw.attachments ?? []).map((attachment) => ({
      ...attachment,
      url: `${PORTAL_API_BASE}/attachments/${encodeURIComponent(attachment.id)}`,
    })),
    plans: raw.plans.map((plan) => ({
      version: plan.version,
      status: plan.status,
      title: `Implementation plan v${plan.version}`,
      content: plan.body,
      created_at: plan.created_at,
      approved_at: plan.approved_at,
      approved_by: plan.approved_at ? actorFromEmail(plan.created_by) : undefined,
    })),
    cycles: raw.cycles.map((cycle) => ({
      id: cycle.id,
      number: cycle.cycle_number,
      status: cycle.status,
      head_sha: cycle.head_sha,
      created_at: cycle.created_at,
      ci_status: cycle.ci_status,
      preview_status: cycle.preview_status,
      qa_status: cycle.qa_status,
      stages: [
        { name: "CI", status: normalizeStage(cycle.ci_status), detail: cycle.last_error },
        { name: "Preview", status: normalizeStage(cycle.preview_status), detail: cycle.last_error },
        { name: "QA", status: normalizeStage(cycle.qa_status), detail: cycle.last_error },
      ],
    })),
    github: latestCycle ? {
      issue_number: latestCycle.issue_number,
      issue_url: latestCycle.issue_url,
      pull_request_number: latestCycle.pr_number,
      pull_request_url: latestCycle.pr_url,
      head_sha: latestCycle.head_sha,
    } : undefined,
    preview: latestCycle?.preview_status && (latestCycle.preview_head_sha || latestCycle.head_sha) ? {
      status: latestCycle.preview_status,
      url: latestCycle.preview_url,
      head_sha: latestCycle.preview_head_sha ?? latestCycle.head_sha!,
      exact_head: Boolean(expectedHead && latestCycle.preview_head_sha === expectedHead),
    } : undefined,
    requester_review: latestCycle?.requester_approved_head_sha ? {
      head_sha: latestCycle.requester_approved_head_sha,
      approved_by: latestCycle.requester_approved_by,
      approved_at: latestCycle.requester_approved_at,
    } : undefined,
    deployments: raw.deployments ?? [],
    deployment: latestCycle?.deployment_status ? {
      status: latestCycle.deployment_status,
      provider: latestCycle.deployment_provider,
      environment: latestCycle.deployment_environment,
      sha: latestCycle.deployment_sha,
      url: latestCycle.deployment_url,
      detail: latestCycle.deployment_detail,
      deployed_at: latestCycle.deployed_at,
    } : undefined,
    qa_run: latestQa ? {
      id: latestQa.id,
      status: latestQa.status,
      head_sha: latestQa.head_sha,
      passed: numericReportValue(report, "passed"),
      failed: numericReportValue(report, "failed"),
      skipped: numericReportValue(report, "skipped"),
      steps: reportSteps.filter((step): step is Record<string, unknown> => Boolean(step && typeof step === "object")).map((step) => ({
        id: typeof step.id === "string" ? step.id : undefined,
        title: typeof step.title === "string" ? step.title : "QA step",
        status: normalizeStage(typeof step.status === "string" ? step.status : undefined),
        duration_ms: typeof step.duration_ms === "number" ? step.duration_ms : undefined,
        detail: typeof step.detail === "string" ? step.detail : undefined,
      })),
    } : undefined,
    artifacts: raw.artifacts.map((artifact) => ({
      id: artifact.id,
      kind: artifact.kind,
      title: artifact.title ?? artifact.object_key?.split("/").at(-1) ?? artifact.kind.replaceAll("_", " "),
      content_type: artifact.content_type,
      head_sha: artifact.head_sha,
      sha256: artifact.sha256,
      created_at: artifact.created_at,
    })),
  };
}

function normalizeStage(status?: string): WorkflowStage["status"] {
  const value = status?.toLowerCase();
  if (["passed", "success", "succeeded", "active", "ready", "completed"].includes(value ?? "")) return "passed";
  if (["failed", "error", "blocked", "stale"].includes(value ?? "")) return "failed";
  if (["running", "building", "previewing", "verifying", "in_progress", "deterministic_passed", "exploratory_queued", "exploratory_running", "repair_queued", "repair_running"].includes(value ?? "")) return "running";
  if (value === "skipped") return "skipped";
  return "pending";
}

export const portalApi = {
  me: async () => {
    const actor = await requestJson<RawActor | PortalUser>("/me");
    if ("subject" in actor) return { id: actor.subject, email: actor.email, name: actor.display_name ?? actor.email, role: actor.role };
    return actor;
  },
  listRequests: async () => unwrapList(
    await requestJson<RawCase[] | ListEnvelope<RawCase, "requests"> | { items: RawCase[] }>("/requests"),
    "requests",
  ).map(mapSummary),
  listRequestsPage: async (page: number, search = "") => {
    const params = new URLSearchParams({ limit: "20", offset: String((page - 1) * 20), search });
    const result = await requestJson<{ items: RawCase[]; total: number }>(`/requests?${params}`);
    return { items: result.items.map(mapSummary), total: result.total };
  },
  getRequest: async (id: string) => mapDetail(await requestJson<RawCaseDetail>(`/requests/${encodeURIComponent(id)}`)),
  updateRequestRequirements: async (id: string, input: CreatePortalRequestInput) =>
    mapDetail(await requestJson<RawCaseDetail>(`/requests/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    })),
  confirmRepository: async (id: string) =>
    mapDetail(await requestJson<RawCaseDetail>(`/requests/${encodeURIComponent(id)}/repository/confirm`, {
      method: "POST",
    })),
  createRequest: async (input: CreatePortalRequestInput) =>
    mapDetail(await requestJson<RawCaseDetail>("/requests", { method: "POST", body: JSON.stringify(input) })),
  deleteRequest: async (id: string) =>
    requestJson<void>(`/requests/${encodeURIComponent(id)}`, { method: "DELETE" }),
  addMessage: (id: string, body: string, requirementField?: string, deferAgent = false) =>
    requestJson<RequestMessage>(`/requests/${encodeURIComponent(id)}/messages`, {
      method: "POST",
      body: JSON.stringify({ body, ...(requirementField ? { requirement_field: requirementField } : {}), ...(deferAgent ? { defer_agent: true } : {}) }),
    }),
  analyzeRequest: (id: string) =>
    requestJson<void>(`/requests/${encodeURIComponent(id)}/analyze`, { method: "POST" }),
  uploadAttachment: async (id: string, input: { message_id?: string; file_name: string; content_type: string; data_base64: string }) => {
    const attachment = await requestJson<Omit<RequestAttachment, "url">>(`/requests/${encodeURIComponent(id)}/attachments`, {
      method: "POST",
      body: JSON.stringify(input),
    });
    return {
      ...attachment,
      url: `${PORTAL_API_BASE}/attachments/${encodeURIComponent(attachment.id)}`,
    };
  },
  approvePlan: async (id: string, version: number) =>
    mapDetail(await requestJson<RawCaseDetail>(`/requests/${encodeURIComponent(id)}/plans/${version}/approve`, { method: "POST" })),
  requestChanges: async (id: string, body: string) =>
    mapDetail(await requestJson<RawCaseDetail>(`/requests/${encodeURIComponent(id)}/change-requests`, {
      method: "POST",
      body: JSON.stringify({ body }),
    })),
  acceptRequest: async (id: string) =>
    mapDetail(await requestJson<RawCaseDetail>(`/requests/${encodeURIComponent(id)}/accept`, { method: "POST" })),
  completeRequest: async (id: string) =>
    mapDetail(await requestJson<RawCaseDetail>(`/requests/${encodeURIComponent(id)}/complete`, { method: "POST" })),
  cancelRequest: async (id: string) =>
    mapDetail(await requestJson<RawCaseDetail>(`/requests/${encodeURIComponent(id)}/cancel`, { method: "POST" })),
  retryRequest: async (id: string) =>
    mapDetail(await requestJson<RawCaseDetail>(`/requests/${encodeURIComponent(id)}/retry`, { method: "POST" })),
  listMergeReadiness: async () => unwrapList(
    await requestJson<RawMergeReadiness[] | { items: RawMergeReadiness[] }>("/merge-readiness"),
    "items",
  ).map((item): MergeReadinessItem => ({
    request_id: item.case_id,
    title: item.title,
    repository: item.repository,
    pull_request_number: item.pr_number ?? numberFromUrl(item.pr_url) ?? 0,
    pull_request_url: item.pr_url ?? item.human_merge_url ?? "",
    head_sha: item.head_sha ?? "",
    base_sha: item.base_sha,
    base_drift: item.base_drift,
    state: mergeDeskState(item),
    request_state: item.status,
    mergeable: item.mergeable,
    exact_head_verified: item.exact_head_verified,
    mergeable_state: item.mergeable_state,
    ci_status: item.ci_status,
    preview_status: item.preview_status,
    qa_status: item.qa_status,
    requester_approved_head_sha: item.requester_approved_head_sha,
    requester_approved_by: item.requester_approved_by,
    requester_approved_at: item.requester_approved_at,
    deployments: item.deployments ?? [],
    conflict_attempts: item.conflict_attempts,
    updated_at: new Date().toISOString(),
  })),
  mergeRequest: async (id: string, merge_method: MergeMethod = "squash") =>
    mapDetail(await requestJson<RawCaseDetail>(`/requests/${encodeURIComponent(id)}/merge`, {
      method: "POST",
      body: JSON.stringify({ merge_method }),
    })),
  listNotifications: async () => unwrapList(
    await requestJson<RawNotification[] | { items: RawNotification[] }>("/notifications"),
    "items",
  ).map((item): PortalNotification => ({ ...item, request_id: item.case_id })),
  markNotificationRead: (id: string) =>
    requestJson<PortalNotification>(`/notifications/${encodeURIComponent(id)}/read`, { method: "POST" }),
  accessArtifact: (id: string) =>
    requestJson<{ url: string; expires_in_seconds: number }>(
      `/artifacts/${encodeURIComponent(id)}/access`,
      { headers: { "X-Netra-Portal-CSRF": "1" } },
    ),
  listRepositories: async () => unwrapList(
    await requestJson<RawRepository[] | { items: RawRepository[] }>("/repositories"),
    "items",
  ).map((item): PortalRepository => ({
    repository: item.repository,
    enabled: item.pilot_enabled,
    qa_config_path: item.qa_config_summary ?? ".netra/qa.toml",
    secret_aliases: item.secret_refs ?? [],
    retention_days: item.retention_days ?? 30,
    max_concurrency: item.concurrency ?? 1,
    updated_at: item.updated_at,
  })),
  updateRepository: async (repository: string, input: UpdatePortalRepositoryInput) => {
    const raw = await requestJson<RawRepository>("/repositories", {
      method: "PATCH",
      body: JSON.stringify({
        repository,
        pilot_enabled: input.enabled,
        qa_config_summary: input.qa_config_path,
        secret_refs: input.secret_aliases,
        retention_days: input.retention_days,
        concurrency: input.max_concurrency,
        // The Rust upsert contract accepts a complete RepositorySettings
        // value, even though it replaces this timestamp with server time.
        updated_at: input.updated_at ?? new Date().toISOString(),
      }),
    });
    return {
      repository: raw.repository,
      enabled: raw.pilot_enabled,
      qa_config_path: raw.qa_config_summary,
      secret_aliases: raw.secret_refs,
      retention_days: raw.retention_days,
      max_concurrency: raw.concurrency,
      updated_at: raw.updated_at,
    };
  },
  listTeam: async () => unwrapList(
    await requestJson<TeamMember[] | { items: TeamMember[] }>("/team"),
    "items",
  ),
  updateTeamMember: (email: string, role: TeamMember["role"], access: TeamMember["access"]) =>
    requestJson<TeamMember>("/team", {
      method: "PATCH",
      body: JSON.stringify({ email, role, access }),
    }),
};

function numberFromUrl(url?: string) {
  if (!url) return undefined;
  const match = url.match(/\/(?:pull|issues)\/(\d+)(?:\/?$|[?#])/);
  return match ? Number(match[1]) : undefined;
}

export function openRequestEvents(
  requestId: string,
  onUpdate: () => void,
  onConnectionChange?: (connected: boolean) => void,
) {
  const source = new EventSource(`${PORTAL_API_BASE}/requests/${encodeURIComponent(requestId)}/events`, {
    withCredentials: true,
  });
  source.onopen = () => onConnectionChange?.(true);
  source.onmessage = () => onUpdate();
  for (const event of [
    "case.created",
    "case.requirements_updated",
    "case.message_added",
    "case.information_answered",
    "case.information_failed",
    "case.changes_requested",
    "case.review_approved",
    "case.accepted",
    "case.cancelled",
    "participant.added",
    "participant.removed",
    "plan.created",
    "plan.approved",
    "workflow.dispatched",
    "workflow.dispatch_reused",
    "workflow.dispatch_failed",
    "workflow.refreshed",
    "workflow.retried",
    "conflict.repair_started",
    "conflict.repair_completed",
    "qa.started",
    "qa.completed",
    "evidence.created",
  ]) source.addEventListener(event, onUpdate);
  source.onerror = () => onConnectionChange?.(false);
  return () => source.close();
}
